/* global FileReader, Image, URL */
import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['input', 'dropArea', 'placeholder', 'filename', 'preview', 'error']

  static values = {
    maxFileSizeBytes: Number,
    maxDimensionPx: Number,
    fileTooLargeMessage: String,
    dimensionsTooLargeMessage: String,
    invalidImageMessage: String
  }

  connect () {
    if (this.hasErrorTarget && this.errorTarget.textContent.trim() !== '') {
      this.markDropAreaInvalid()
    }
  }

  open (event) {
    event.preventDefault()
    this.inputTarget.click()
  }

  dragover (event) {
    event.preventDefault()
    this.dropAreaTarget.dataset.dragging = 'true'
  }

  dragleave (event) {
    event.preventDefault()
    delete this.dropAreaTarget.dataset.dragging
  }

  drop (event) {
    event.preventDefault()
    delete this.dropAreaTarget.dataset.dragging

    const files = event.dataTransfer.files
    if (files.length === 0) return

    this.inputTarget.files = files
    this.render()
  }

  change () {
    this.render()
  }

  async render () {
    const file = this.inputTarget.files[0]
    if (!file) return

    const validationError = await this.validationErrorFor(file)
    if (validationError) {
      this.showError(validationError)
      return
    }

    this.clearError()

    if (this.hasFilenameTarget) this.filenameTarget.textContent = file.name
    if (this.hasPlaceholderTarget) this.placeholderTarget.hidden = true

    if (this.hasPreviewTarget && file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.previewTarget.src = e.target.result
        this.previewTarget.hidden = false
      }
      reader.readAsDataURL(file)
    }
  }

  async validationErrorFor (file) {
    if (this.maxFileSizeBytesValue > 0 && file.size > this.maxFileSizeBytesValue) {
      return this.fileTooLargeMessageValue
    }

    if (this.maxDimensionPxValue > 0 && file.type !== 'image/svg+xml') {
      const dimensions = await this.readImageDimensions(file)
      if (!dimensions) return this.invalidImageMessageValue

      if (dimensions.width > this.maxDimensionPxValue || dimensions.height > this.maxDimensionPxValue) {
        return this.dimensionsTooLargeMessageValue
      }
    }

    return null
  }

  readImageDimensions (file) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file)
      const image = new Image()
      image.onload = () => {
        URL.revokeObjectURL(url)
        resolve({ width: image.naturalWidth, height: image.naturalHeight })
      }
      image.onerror = () => {
        URL.revokeObjectURL(url)
        resolve(null)
      }
      image.src = url
    })
  }

  showError (message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.hidden = false
    }
    this.markDropAreaInvalid()
    this.inputTarget.value = ''
    if (this.hasFilenameTarget) this.filenameTarget.textContent = ''
    if (this.hasPlaceholderTarget) this.placeholderTarget.hidden = false
    if (this.hasPreviewTarget) this.previewTarget.hidden = true
  }

  clearError () {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ''
      this.errorTarget.hidden = true
    }
    this.markDropAreaValid()
  }

  markDropAreaInvalid () {
    this.dropAreaTarget.classList.remove('border-gray-200', 'hover:border-team-primary')
    this.dropAreaTarget.classList.add('border-red-500', 'bg-red-50')
    this.dropAreaTarget.setAttribute('aria-invalid', 'true')
  }

  markDropAreaValid () {
    this.dropAreaTarget.classList.remove('border-red-500', 'bg-red-50')
    this.dropAreaTarget.classList.add('border-gray-200', 'hover:border-team-primary')
    this.dropAreaTarget.removeAttribute('aria-invalid')
  }
}
